import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ResponseSuccess,
  ResponseErrorWithHandled,
} from '../../../../shared/types';
import { PaginationQueryParams } from '../../../../shared/types/request';
import {
  getCustomCallOutcomes,
  getDefaultCallOutcomes,
  getAllCallOutcomes,
  createCallOutcome,
  updateCallOutcome,
  deleteCallOutcome,
} from './helpers/call-outcome.api';
import { CreateCallOutcomePayload, UpdateCallOutcomePayload } from './types';

// Fetch custom call outcomes with pagination
export const getCustomCallOutcomesRequest = createAsyncThunk<
  ResponseSuccess,
  PaginationQueryParams & { search?: string },
  { rejectValue: ResponseErrorWithHandled }
>(
  'callOutcome/getCustomCallOutcomesRequest',
  async ({ search, ...args }, thunkAPI) => {
    try {
      const requestArgs = {
        ...args,
        ...(search && { search }),
      };
      return await getCustomCallOutcomes(requestArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

// Fetch default (system) call outcomes with pagination
export const getDefaultCallOutcomesRequest = createAsyncThunk<
  ResponseSuccess,
  PaginationQueryParams & { search?: string },
  { rejectValue: ResponseErrorWithHandled }
>(
  'callOutcome/getDefaultCallOutcomesRequest',
  async ({ search, ...args }, thunkAPI) => {
    try {
      const requestArgs = {
        ...args,
        ...(search && { search }),
      };
      return await getDefaultCallOutcomes(requestArgs);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

// Fetch all call outcomes (no pagination — for delete modal dropdown)
export const getAllCallOutcomesRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('callOutcome/getAllCallOutcomesRequest', async (_, thunkAPI) => {
  try {
    return await getAllCallOutcomes();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Create custom call outcome
export const createCallOutcomeRequest = createAsyncThunk<
  ResponseSuccess,
  CreateCallOutcomePayload,
  { rejectValue: ResponseErrorWithHandled }
>('callOutcome/createCallOutcomeRequest', async (payload, thunkAPI) => {
  try {
    return await createCallOutcome(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Update call outcome
type UpdateCallOutcomeThunkArgs = UpdateCallOutcomePayload & {
  callOutcomeId: number;
};

export const updateCallOutcomeRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateCallOutcomeThunkArgs,
  { rejectValue: ResponseErrorWithHandled }
>(
  'callOutcome/updateCallOutcomeRequest',
  async ({ callOutcomeId, ...payload }, thunkAPI) => {
    try {
      return await updateCallOutcome(callOutcomeId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

// Delete call outcome
type DeleteCallOutcomeThunkArgs = {
  callOutcomeId: number;
  replacementCallOutcomeId: number;
};

export const deleteCallOutcomeRequest = createAsyncThunk<
  ResponseSuccess,
  DeleteCallOutcomeThunkArgs,
  { rejectValue: ResponseErrorWithHandled }
>(
  'callOutcome/deleteCallOutcomeRequest',
  async ({ callOutcomeId, replacementCallOutcomeId }, thunkAPI) => {
    try {
      return await deleteCallOutcome(callOutcomeId, replacementCallOutcomeId);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);
